import { supabase } from '@/integrations/supabase/client';

/**
 * ----------------------------------------------------------------
 * ChipChip Sales Data Service
 * ----------------------------------------------------------------
 * Integrates with ChipChip API to fetch sales data for benchmark comparison
 */

// ChipChip API Response Types
interface ColumnMetadata {
  column_name: string;
  name: string;
  type: string;
  type_generic: number;
  is_dttm: boolean;
}

interface ChipChipProductData {
  product_name: string;
  date: string;
  order_type: string;
  price: number;
  num_order: number;
  quantity: number;
}

interface QueryExtra {
  progress: null;
  columns: ColumnMetadata[];
}

interface QueryInfo {
  changed_on: string;
  dbId: number;
  db: string;
  endDttm: number;
  errorMessage: string | null;
  executedSql: string;
  id: string;
  queryId: number;
  limit: number;
  limitingFactor: string;
  progress: number;
  rows: number;
  catalog: string | null;
  schema: string;
  ctas: boolean;
  serverId: number;
  sql: string;
  sqlEditorId: string | null;
  startDttm: number;
  state: 'success' | 'failed' | 'running';
  tab: string;
  tempSchema: string | null;
  tempTable: string | null;
  userId: number;
  user: string;
  resultsKey: string;
  trackingUrl: string | null;
  extra: QueryExtra;
}

interface ChipChipApiResponse {
  query_id: number;
  status: 'success' | 'failed' | 'running';
  data: ChipChipProductData[];
  selected_columns: ColumnMetadata[];
  expanded_columns: any[];
  query: QueryInfo;
}

// Generate random client ID for API requests
const generateClientId = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 11; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

/**
 * Fetches ChipChip sales data via Edge Function
 */
export async function fetchChipChipSalesData(
  productName: string,
  startDate: string,
  endDate: string
): Promise<ChipChipProductData[]> {
  try {
    const { data, error } = await supabase.functions.invoke('chipchip-sales', {
      body: {
        productName,
        startDate,
        endDate,
      },
    });

    if (error) {
      console.error('ChipChip API error:', error);
      throw new Error(`ChipChip API error: ${error.message}`);
    }

    return data?.salesData || [];
  } catch (error) {
    console.error('Error fetching ChipChip sales data:', error);
    return []; // Return empty array on error to not break benchmark comparison
  }
}

/**
 * Transform ChipChip data to benchmark PriceData format
 */
export function transformChipChipData(chipchipData: ChipChipProductData[]) {
  return chipchipData.map(item => ({
    date: item.date,
    price: item.price,
    location: item.order_type,
    product_name: item.product_name,
    source: 'chipchip-sales',
  }));
}

export const chipchipSalesService = {
  fetchChipChipSalesData,
  transformChipChipData,
};